/**
 * useNotificationEvents - Hook that subscribes to Matrix client events
 * and triggers notifications for new messages, invites, reactions, and mentions when appropriate.
 *
 * This hook handles:
 * 1. Live encrypted messages (RoomEvent.Timeline → pendingEncryptedEventIds → MatrixEventEvent.Decrypted)
 * 2. Live unencrypted messages (RoomEvent.Timeline with m.room.message + liveEvent=true)
 * 3. Membership events (RoomMemberEvent.Membership) for invites
 * 4. Reaction events (RoomEvent.Timeline for m.reaction) for reactions on own messages
 * 5. Room notification cancellation when entering a room
 * 6. Read marker updates (RoomEvent.AccountData for m.fully_read) for auto-cancel
 *
 * Features:
 * - Skips notifications when sender is current user
 * - Skips notifications when currently viewing that room
 * - Respects browser notification master toggle from SettingsContext
 * - Respects room mute state (DONT_NOTIFY, MENTIONS_ONLY, NOTIFY)
 * - Extracts sender name, avatar, and message preview for notifications
 * - Uses conversation/room name as title for all room types
 * - Auto-cancels notifications when read marker advances past them
 *
 * Note: Invite notifications are NOT filtered by mute state since
 * you cannot mute a room you haven't joined yet.
 */

import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MatrixEventEvent, RoomMemberEvent, RoomEvent, ClientEvent, SyncState } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useDisambiguation } from '../context/DisambiguationContext';
import { useI18n } from '../context/I18nContext';
import { useSettings } from '../context/SettingsContext';
import { useNotifications } from '../context/NotificationContext';
import { useNavigation } from '../context/NavigationContext';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';
import { getRoomPushRuleState, PushRuleState } from '../utils/pushRuleState';
import { eventMentionsUser } from '../utils/mentionDetection';
import { shouldBlockInvite, extractDomain } from '../utils/inviteFiltering';
import timInformationService from '../services/timInformationService';
import { acquireAvatar } from '../utils/avatarManager';

/**
 * Module-level Set tracking event IDs from live encrypted events awaiting decryption.
 * Only events arriving via live sync (RoomEvent.Timeline with liveEvent=true) are added.
 * When MatrixEventEvent.Decrypted fires, we check this set — if present, it's a live
 * message and we show a notification. If absent, it's from pagination/key-backup/cache.
 */
const pendingEncryptedEventIds = new Set();
const PENDING_ENCRYPTED_MAX = 2000;
const PROCESSED_EVENT_MAX = 5000;
const PROCESSED_EVENT_TRIM_TO = 3000;

/** Reset the pending set. Exported for test isolation only. */
export const __resetPendingEncryptedForTesting = () => pendingEncryptedEventIds.clear();
/** Check if an event ID is in the pending set. Exported for test verification only. */
export const isPendingEncryptedEvent = (eventId) => pendingEncryptedEventIds.has(eventId);

/**
 * Extract room ID from URL path for /conversations/:id or /channels/:id routes
 * @param {string} pathname - URL pathname
 * @returns {string|null} Room ID or null
 */
const extractRoomIdFromPath = (pathname) => {
  const conversationMatch = pathname.match(/^\/conversations\/(.+)$/);
  if (conversationMatch) {
    try {
      return decodeURIComponent(conversationMatch[1]);
    } catch {
      return null;
    }
  }

  const channelMatch = pathname.match(/^\/channels\/(.+)$/);
  if (channelMatch) {
    try {
      return decodeURIComponent(channelMatch[1]);
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Get a message preview from an event, truncated to 70 characters
 * @param {MatrixEvent} event - Matrix event
 * @param {Function} t - Translation function
 * @returns {string} Message preview
 */
const getMessagePreview = (event, t) => {
  // Try clear content first (for decrypted messages)
  const clearContent = event.getClearContent && event.getClearContent();
  const rawContent = clearContent || event.getContent();
  // For edit events, use m.new_content which has the actual updated text
  // (top-level body is prefixed with "* " per Matrix spec fallback)
  const content = rawContent?.['m.new_content'] || rawContent;

  if (!content) {
    return '';
  }

  // Handle message types
  const msgtype = content.msgtype;

  if (msgtype === 'm.text') {
    const body = content.body || '';
    return body.length > 70 ? body.substring(0, 70) + '...' : body;
  }

  if (msgtype === 'm.image') {
    return t('Notifications.sentAnImage');
  }

  if (msgtype === 'm.file') {
    return t('Notifications.sentAFile');
  }

  if (msgtype === 'm.video') {
    return t('Notifications.sentAVideo');
  }

  if (msgtype === 'm.audio') {
    return t('Notifications.sentAnAudioMessage');
  }

  if (msgtype === 'm.location') {
    return t('Notifications.sharedLocation');
  }

  // Fallback
  const body = content.body || '';
  return body.length > 50 ? body.substring(0, 50) + '...' : body;
};

/**
 * Fetch avatar as blob URL for COEP compatibility.
 * Uses avatarManager to convert Matrix media URLs to blob URLs,
 * avoiding COEP blocking when passing to Notification API.
 *
 * @param {MatrixClient} client - Matrix client instance
 * @param {string} senderId - Matrix user ID
 * @param {string} senderName - Display name for fallback
 * @returns {Promise<string|null>} Blob URL or null on failure
 */
const getNotificationAvatarBlobUrl = async (client, senderId, senderName) => {
  try {
    return await acquireAvatar(client, senderId, senderName, 96, 'user');
  } catch (error) {
    logger.warn('[useNotificationEvents] Failed to fetch avatar blob URL:', error);
    return null;
  }
};

/**
 * Hook that subscribes to Matrix events and triggers notifications
 * when appropriate based on settings and current view state.
 */
export const useNotificationEvents = () => {
  const client = useMatrixClient();
  const { needsDisambiguation } = useDisambiguation();
  const { t } = useI18n();
  const { settings } = useSettings();
  const { showNotification, cancelNotification, cancelRoomNotifications, isAppVisible } = useNotifications();
  const { navigateToConversation, navigateToChannel } = useNavigation();
  const location = useLocation();

  // Track handlers with refs to ensure proper cleanup
  const decryptedHandlerRef = useRef(null);
  const membershipHandlerRef = useRef(null);
  const reactionHandlerRef = useRef(null);
  const timelineNotifHandlerRef = useRef(null);
  const accountDataHandlerRef = useRef(null);

  // Keep track of current room ID from URL
  const currentRoomIdRef = useRef(null);

  // Track isAppVisible in a ref to avoid callback re-creation on visibility changes
  // This prevents Matrix event listener re-subscription when tab visibility changes
  const isAppVisibleRef = useRef(isAppVisible);

  // Keep the ref in sync with the state value - useLayoutEffect ensures synchronous update
  // This prevents race conditions where event handlers read stale ref values
  useLayoutEffect(() => {
    isAppVisibleRef.current = isAppVisible;
  }, [isAppVisible]);

  // Track read markers per room: Map<roomId, eventId>
  const readMarkersRef = useRef(new Map());

  // Track rooms we've subscribed to for account data
  const subscribedRoomsRef = useRef(new Set());

  // Track needsDisambiguation in a ref to avoid callback re-creation on disambiguation changes
  // This prevents Matrix event listener re-subscription when disambiguation map updates
  const needsDisambiguationRef = useRef(needsDisambiguation);

  useLayoutEffect(() => {
    needsDisambiguationRef.current = needsDisambiguation;
  }, [needsDisambiguation]);

  // Track pending invite notifications by roomId for cancellation on accept/decline
  const pendingInviteNotificationsRef = useRef(new Map()); // Map<roomId, eventId>

  // Only process notifications after initial sync completes (SyncState.Syncing).
  // During initial sync (PREPARED), all events arrive with liveEvent=true but are not "new".
  const initialSyncCompleteRef = useRef(false);

  useEffect(() => {
    if (!client) return;
    const onSync = (state) => {
      if (state === SyncState.Syncing) {
        initialSyncCompleteRef.current = true;
      }
    };
    // Check if already syncing when hook mounts
    if (client.getSyncState() === SyncState.Syncing) {
      initialSyncCompleteRef.current = true;
    }
    client.on(ClientEvent.Sync, onSync);
    return () => client.off(ClientEvent.Sync, onSync);
  }, [client]);

  // Set of event IDs already processed by handleDecryptedEvent.
  // Initialized from sessionStorage so that events processed before a page
  // reload are recognized after it (prevents re-notification for re-decrypted
  // events). Persisted to sessionStorage on beforeunload.
  const processedEventIdsRef = useRef((() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem('notif_processed_events') || '[]'));
    } catch {
      return new Set();
    }
  })());

  // Extract room ID from current path
  const currentRoomId = extractRoomIdFromPath(location.pathname);

  // Update ref when path changes
  useEffect(() => {
    const previousRoomId = currentRoomIdRef.current;
    currentRoomIdRef.current = currentRoomId;

    // Cancel notifications for the room we just entered
    // Only cancel if app is in foreground - don't cancel when app is in background
    // (e.g., when Matrix sync updates the route but user isn't actually viewing the room)
    if (currentRoomId && currentRoomId !== previousRoomId && isAppVisibleRef.current) {
      logger.debug(`[useNotificationEvents] Entered room ${currentRoomId}, cancelling notifications`);
      cancelRoomNotifications(currentRoomId);
    }
  }, [currentRoomId, cancelRoomNotifications]);

  /**
   * Shared notification processing logic for both encrypted (after Decrypted)
   * and unencrypted (from Timeline) message paths.
   */
  const processMessageNotification = useCallback((event) => {
    const roomId = event.getRoomId();
    const eventId = event.getId();
    if (!roomId || !eventId) return;

    // Detect edit events (m.replace)
    const clearContent = event.getClearContent && event.getClearContent();
    const content = clearContent || event.getContent();
    const relatesTo = content?.['m.relates_to'];
    const isEdit = relatesTo?.rel_type === 'm.replace';
    const originalEventId = isEdit ? relatesTo.event_id : null;

    // Skip if this event was already processed in this or a prior session
    if (processedEventIdsRef.current.has(eventId)) {
      logger.debug('[useNotificationEvents] Skipping notification - event already processed');
      return;
    }

    // Mark as processed immediately (regardless of whether we show a notification)
    if (processedEventIdsRef.current.size > PROCESSED_EVENT_MAX) {
      const entries = Array.from(processedEventIdsRef.current);
      processedEventIdsRef.current = new Set(entries.slice(-PROCESSED_EVENT_TRIM_TO));
    }
    processedEventIdsRef.current.add(eventId);

    const senderId = event.getSender();
    const myUserId = client.getUserId();

    // Skip if sender is current user
    if (senderId === myUserId) {
      logger.debug('[useNotificationEvents] Skipping notification - own message');
      return;
    }

    // Skip if currently viewing that room AND app is visible
    if (roomId === currentRoomIdRef.current && isAppVisibleRef.current) {
      logger.debug(`[useNotificationEvents] Skipping notification - viewing room ${roomId} and app is visible`);
      return;
    }

    // Check room mute state
    const muteState = getRoomPushRuleState(client, roomId);
    if (muteState === PushRuleState.DONT_NOTIFY) {
      logger.debug('[useNotificationEvents] Skipping notification - room is muted');
      return;
    }

    // Get the room object (needed for mention detection with display names)
    const room = client.getRoom(roomId);
    if (!room) {
      logger.debug('[useNotificationEvents] Skipping notification - room not found');
      return;
    }

    // Detect if message mentions user (for notification format and MENTIONS_ONLY filtering)
    // For edit events, check mentions in m.new_content (per MSC3952,
    // top-level m.mentions is empty for edits to prevent re-notification)
    const mentionContent = isEdit ? (content['m.new_content'] || content) : undefined;
    const isMentioned = eventMentionsUser(event, myUserId, {
      room,
      displayName: client.getUser(myUserId)?.displayName,
      contentOverride: mentionContent,
    });

    // For mentions-only rooms, check if this message mentions the user
    if (muteState === PushRuleState.MENTIONS_ONLY && !isMentioned) {
      logger.debug('[useNotificationEvents] Skipping notification - mentions only, not mentioned');
      return;
    }

    // Check notification settings: skip if neither message nor mention notifications are enabled
    const shouldNotifyForMention = isMentioned && settings.notifyMentions;
    const shouldNotifyForMessage = settings.notifyNewMessages;

    if (!shouldNotifyForMention && !shouldNotifyForMessage) {
      logger.debug('[useNotificationEvents] Skipping - notifications disabled for this event type');
      return;
    }

    // Get sender info
    const sender = room.getMember(senderId);
    const senderName = sender?.name || sender?.rawDisplayName || senderId;

    // Get message preview, with (edited) prefix for edit events
    const rawPreview = getMessagePreview(event, t);
    const messagePreview = isEdit ? `(${t('Notifications.editedPrefix')}) ${rawPreview}` : rawPreview;

    // Build notification title and determine navigation type
    const isDM = matrixService.isDMRoom(roomId);
    const title = room.name || t('Notifications.newMessage');

    // Build notification body - use "mentioned you" format for mentions
    let body;
    if (isMentioned && settings.notifyMentions) {
      body = t('Notifications.mentionedYouWithPreview', { senderName, messagePreview });
    } else {
      body = `${senderName}: ${messagePreview}`;
    }

    // Navigate to the room and focus on the specific message when notification is clicked
    // For edit events, navigate to the original message since edits are merged into it in the UI
    const clickTargetId = originalEventId || eventId;
    const onClick = () => {
      if (isDM) {
        navigateToConversation(roomId, { messageId: clickTargetId });
      } else {
        navigateToChannel(roomId, { messageId: clickTargetId });
      }
    };

    // Async notification logic - fetch avatar as blob URL for COEP compatibility
    (async () => {
      const senderAvatarBlobUrl = await getNotificationAvatarBlobUrl(client, senderId, senderName);

      logger.debug(`[useNotificationEvents] Showing notification for event ${eventId} in room ${roomId}`);

      showNotification({
        eventId,
        roomId,
        title,
        body,
        icon: senderAvatarBlobUrl,
        tag: originalEventId || undefined,
        onClick,
        playSound: settings.notifySound,
      });
    })();
  }, [client, settings.notifySound, settings.notifyNewMessages, settings.notifyMentions, showNotification, navigateToConversation, navigateToChannel, t]);

  /**
   * Handle RoomEvent.Timeline for notification tracking.
   * - Encrypted live events: track in pendingEncryptedEventIds for later Decrypted handling
   * - Unencrypted live events: process notification directly
   */
  const handleTimelineForNotifications = useCallback((event, room, toStartOfTimeline, removed, data) => {
    if (!client || !data?.liveEvent || toStartOfTimeline) return;
    if (!initialSyncCompleteRef.current) return;

    const eventType = event.getType();

    if (eventType === 'm.room.encrypted') {
      const eventId = event.getId();
      if (!eventId) return;
      if (pendingEncryptedEventIds.size > PENDING_ENCRYPTED_MAX) {
        const entries = Array.from(pendingEncryptedEventIds);
        pendingEncryptedEventIds.clear();
        entries.slice(-1000).forEach(id => pendingEncryptedEventIds.add(id));
      }
      pendingEncryptedEventIds.add(eventId);
      return;
    }

    if (eventType === 'm.room.message') {
      processMessageNotification(event);
    }
  }, [client, processMessageNotification]);

  /**
   * Handle decrypted message events — thin wrapper that checks pendingEncryptedEventIds
   */
  const handleDecryptedEvent = useCallback((event, err) => {
    if (!client) return;
    if (!initialSyncCompleteRef.current) return;

    const roomId = event.getRoomId();
    if (!roomId) return;
    const eventId = event.getId();
    if (!eventId) return;

    // Only notify for events tracked as live encrypted events
    if (!pendingEncryptedEventIds.has(eventId)) {
      logger.debug('[useNotificationEvents] Skipping - not a live sync event');
      return;
    }
    pendingEncryptedEventIds.delete(eventId);

    // Skip decryption failures
    if (err || event.isDecryptionFailure?.()) {
      logger.debug('[useNotificationEvents] Skipping - decryption failed');
      return;
    }

    // Only process messages — skip reactions, stickers, state events
    if (event.getType() !== 'm.room.message') {
      logger.debug(`[useNotificationEvents] Skipping decrypted non-message: ${event.getType()}`);
      return;
    }

    processMessageNotification(event);
  }, [client, processMessageNotification]);

  /**
   * Handle membership events for invites
   * - Shows notification when user is invited
   * - Cancels notification when user accepts (join) or declines (leave)
   */
  const handleMemberEvent = useCallback((event, member) => {
    if (!client) return;
    if (!initialSyncCompleteRef.current) return;

    const myUserId = client.getUserId();

    // Only handle when we are the one being invited/joining/leaving
    if (member.userId !== myUserId) {
      return;
    }

    const roomId = member.roomId;

    // Handle invite acceptance or decline - cancel the notification
    if (member.membership === 'join' || member.membership === 'leave') {
      const pendingEventId = pendingInviteNotificationsRef.current.get(roomId);
      if (pendingEventId) {
        cancelNotification(pendingEventId);
        pendingInviteNotificationsRef.current.delete(roomId);
        logger.debug(`[useNotificationEvents] Cancelled invite notification for room ${roomId} (membership: ${member.membership})`);
      }
      // Catch-all: cancel any room notifications not tracked in the pending map
      // (e.g. notifications shown before the invite tracking was set up)
      cancelRoomNotifications(roomId);
      return;
    }

    // Only show notification for invite membership
    if (member.membership !== 'invite') {
      return;
    }

    // Skip if user disabled invite notifications
    if (!settings.notifyInvites) {
      logger.debug('[useNotificationEvents] Skipping notification - invite notifications disabled');
      return;
    }

    // Check if this invite should be blocked based on Incoming Invites settings
    // This prevents showing notifications for invites that will be auto-rejected.
    //
    // Group entries require an async classification lookup (tim-information isInsurance).
    // To keep the notification path synchronous, we use a non-throwing cache peek:
    // - Cache hit: classification is applied and the notification is suppressed/shown correctly.
    // - Cache miss: the sync resolver returns null, shouldBlockInvite may return null
    //   (ambiguous), in which case we fall through and show the notification. useInviteFiltering
    //   will then auto-decline asynchronously; the user sees at most a brief flash on the FIRST
    //   invite from each insurance homeserver per session (cache TTL = 1h).
    const inviterId = event.getSender();
    if (inviterId) {
      const syncGroupResolver = (userId, groupName) => {
        if (groupName !== 'isInsuredPerson') return null;
        const hs = extractDomain(userId);
        if (!hs) return null;
        return timInformationService.peekCache(hs);
      };
      const blockDecision = shouldBlockInvite(inviterId, settings, { groupResolver: syncGroupResolver });
      if (blockDecision === true) {
        logger.debug('[useNotificationEvents] Skipping notification - invite will be auto-rejected');
        return;
      }
      // blockDecision === null means ambiguous (cache miss on a group entry). Show the
      // notification; useInviteFiltering will auto-decline asynchronously if appropriate.
    }

    const eventId = event.getId() || `invite-${roomId}-${Date.now()}`;

    // Get the room to find inviter info
    const room = client.getRoom(roomId);
    const roomName = room?.name || t('Notifications.aRoom');

    // Get inviter display name (inviterId already extracted above for blocking check)
    let inviterName = inviterId;

    if (room && inviterId) {
      const inviter = room.getMember(inviterId);
      const baseName = inviter?.name || inviter?.rawDisplayName || inviterId;
      inviterName = needsDisambiguationRef.current(inviterId) ? `${baseName} (${inviterId})` : baseName;
    }

    const title = t('Notifications.newInvitation');
    const body = t('Notifications.invitedYouTo', { inviterName, roomName });

    // Navigate to the room/channel when notification is clicked
    // Evaluate isDM at click time (not creation time) because room state
    // may not have is_direct flag set yet when the invite event fires
    const onClick = () => {
      if (matrixService.isDMRoom(roomId)) {
        navigateToConversation(roomId);
      } else {
        navigateToChannel(roomId);
      }
    };

    logger.debug(`[useNotificationEvents] Showing invite notification for room ${roomId}`);

    // Track the notification so we can cancel it when invite is accepted/declined
    pendingInviteNotificationsRef.current.set(roomId, eventId);

    showNotification({
      eventId,
      roomId,
      title,
      body,
      onClick,
      playSound: settings.notifySound,
    });
  }, [client, settings.notifySound, settings.notifyInvites, settings.firstContactDefaultPermission, settings.firstContactBlockedList, settings.firstContactAllowedList, showNotification, cancelNotification, cancelRoomNotifications, navigateToConversation, navigateToChannel, t]);

  /**
   * Handle reaction events (m.reaction)
   * Notifies when someone reacts to the current user's own message
   */
  const handleReactionEvent = useCallback((event, room, toStartOfTimeline, removed, data) => {
    if (!client) return;
    if (!initialSyncCompleteRef.current) return;

    // Skip historical events (backfilled)
    if (toStartOfTimeline) return;

    // Only process live events (skip forward pagination)
    if (!data?.liveEvent) return;

    // Only handle m.reaction events
    if (event.getType() !== 'm.reaction') return;

    // Check if reaction notifications are enabled
    if (!settings.notifyReactions) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - reaction notifications disabled');
      return;
    }

    const roomId = event.getRoomId();
    if (!roomId) return;

    const senderId = event.getSender();
    const myUserId = client.getUserId();

    // Skip if we can't determine current user
    if (!myUserId) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - no current user ID');
      return;
    }

    // Skip own reactions
    if (senderId === myUserId) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - own reaction');
      return;
    }

    // Skip if currently viewing that room AND app is visible
    // When app is minimized/tabbed away, still show notifications for open room
    if (roomId === currentRoomIdRef.current && isAppVisibleRef.current) {
      logger.debug(`[useNotificationEvents] Skipping reaction notification - viewing room ${roomId} and app is visible`);
      return;
    }

    // Check room mute state - skip if room is muted or mentions-only
    // (reactions are not considered mentions)
    const muteState = getRoomPushRuleState(client, roomId);
    if (muteState === PushRuleState.DONT_NOTIFY) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - room is muted');
      return;
    }
    if (muteState === PushRuleState.MENTIONS_ONLY) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - room is mentions-only');
      return;
    }

    // Get reaction details from the event content
    const content = event.getContent();
    if (!content) {
      logger.debug('[useNotificationEvents] Skipping reaction - no content');
      return;
    }
    const relatesTo = content['m.relates_to'];

    // Validate reaction structure
    if (!relatesTo || relatesTo.rel_type !== 'm.annotation') {
      logger.debug('[useNotificationEvents] Skipping reaction - invalid structure');
      return;
    }

    const targetEventId = relatesTo.event_id;
    const emoji = relatesTo.key;

    if (!targetEventId || !emoji) {
      logger.debug('[useNotificationEvents] Skipping reaction - missing target or emoji');
      return;
    }

    // Get the room and find the target message
    const roomObj = client.getRoom(roomId);
    if (!roomObj) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - room not found');
      return;
    }

    // Find the target event in the room's timeline
    const timeline = roomObj.getLiveTimeline();
    const events = timeline?.getEvents() || [];
    const targetEvent = events.find(e => e.getId() === targetEventId);

    // Skip if target event not found
    if (!targetEvent) {
      logger.debug('[useNotificationEvents] Skipping reaction notification - target event not found in timeline');
      return;
    }

    // Determine message owner for attribution
    const targetSenderId = targetEvent.getSender();
    const isOwnMessage = targetSenderId === myUserId;
    const targetMember = roomObj.getMember(targetSenderId);
    const targetSenderName = targetMember?.name || targetMember?.rawDisplayName || targetSenderId;

    // Get message preview for notification (first 70 chars)
    // Try clear content first (for decrypted messages)
    const clearContent = targetEvent.getClearContent && targetEvent.getClearContent();
    const targetContent = clearContent || targetEvent.getContent();
    const msgType = targetContent?.msgtype;

    let messagePreview;
    if (msgType === 'm.image') {
      messagePreview = isOwnMessage
        ? t('Notifications.yourImage')
        : t('Notifications.othersImage', { name: targetSenderName });
    } else if (msgType === 'm.video') {
      messagePreview = isOwnMessage
        ? t('Notifications.yourVideo')
        : t('Notifications.othersVideo', { name: targetSenderName });
    } else {
      const messageContent = targetContent?.body || '';
      if (messageContent) {
        const truncated = messageContent.length > 70
          ? messageContent.substring(0, 70) + '...'
          : messageContent;
        // For own message, just show content; for other's message, show owner name
        if (isOwnMessage) {
          messagePreview = t('Notifications.yourMessageWithContent', { content: truncated });
        } else {
          messagePreview = t('Notifications.othersMessageWithContent', { name: targetSenderName, content: truncated });
        }
      } else {
        messagePreview = isOwnMessage
          ? t('Notifications.yourMessage')
          : t('Notifications.othersMessage', { name: targetSenderName });
      }
    }

    // Get sender info for the notification
    const sender = roomObj.getMember(senderId);
    const senderName = sender?.name || sender?.rawDisplayName || senderId;

    const reactionEventId = event.getId() || `reaction-${roomId}-${Date.now()}`;

    // Determine notification title and body
    const isDM = matrixService.isDMRoom(roomId);
    const notificationTitle = roomObj.name || t('Notifications.reaction');
    const notificationBody = t('Notifications.reactedWith', { senderName, emoji, messagePreview });

    // Navigate to the room when notification is clicked
    const onClick = () => {
      if (isDM) {
        navigateToConversation(roomId, { messageId: targetEventId });
      } else {
        navigateToChannel(roomId, { messageId: targetEventId });
      }
    };

    // Async notification logic - fetch avatar as blob URL for COEP compatibility
    // Using IIFE since event handlers are synchronous
    (async () => {
      // Fetch avatar as blob URL to avoid COEP blocking on Windows
      const senderAvatarBlobUrl = await getNotificationAvatarBlobUrl(client, senderId, senderName);

      logger.debug(`[useNotificationEvents] Showing reaction notification: ${emoji} from ${senderName} in room ${roomId}`);

      showNotification({
        eventId: reactionEventId,
        roomId,
        title: notificationTitle,
        body: notificationBody,
        icon: senderAvatarBlobUrl,
        onClick,
        playSound: settings.notifySound,
      });
    })();
  }, [client, settings.notifySound, settings.notifyReactions, showNotification, navigateToConversation, navigateToChannel, t]);

  /**
   * Handle account data events for read marker updates (m.fully_read)
   * When read markers advance, cancel notifications for events that are now read
   */
  const handleAccountData = useCallback((event, room) => {
    if (!client) return;

    // Only handle m.fully_read events
    if (event.getType() !== 'm.fully_read') return;

    const roomId = room?.roomId;
    if (!roomId) return;

    const newReadMarkerId = event.getContent()?.event_id;
    if (!newReadMarkerId) return;

    const previousMarkerId = readMarkersRef.current.get(roomId);
    readMarkersRef.current.set(roomId, newReadMarkerId);

    // If no previous marker, this is first time we're seeing it - nothing to cancel
    if (!previousMarkerId) {
      logger.debug(`[useNotificationEvents] Initial read marker for room ${roomId}: ${newReadMarkerId}`);
      return;
    }

    // If marker hasn't changed, nothing to do
    if (previousMarkerId === newReadMarkerId) {
      return;
    }

    // Get the room's timeline to find events between markers
    const roomObj = client.getRoom(roomId);
    if (!roomObj) {
      logger.debug(`[useNotificationEvents] Room not found for read marker update: ${roomId}`);
      return;
    }

    const timeline = roomObj.getLiveTimeline();
    const events = timeline?.getEvents() || [];

    if (events.length === 0) {
      logger.debug(`[useNotificationEvents] No events in timeline for room ${roomId}`);
      return;
    }

    // Find and cancel notifications for events between previous and new marker
    let foundPrevious = false;
    let cancelledCount = 0;

    for (const timelineEvent of events) {
      const eventId = timelineEvent.getId();

      // Start cancelling after we pass the previous marker
      if (eventId === previousMarkerId) {
        foundPrevious = true;
        continue;
      }

      // Cancel notifications for events between old and new marker
      if (foundPrevious) {
        cancelNotification(eventId);
        cancelledCount++;
      }

      // Stop when we reach the new marker
      if (eventId === newReadMarkerId) {
        break;
      }
    }

    if (cancelledCount > 0) {
      logger.debug(`[useNotificationEvents] Read marker advanced in ${roomId}, cancelled ${cancelledCount} notification(s)`);
    }
  }, [client, cancelNotification]);

  // Persist processed event IDs to sessionStorage on page unload.
  // This survives reloads so re-decrypted events are recognized.
  useEffect(() => {
    const handler = () => {
      try {
        const entries = Array.from(processedEventIdsRef.current);
        // Keep only the most recent entries to bound storage usage
        const toStore = entries.length > PROCESSED_EVENT_TRIM_TO ? entries.slice(-PROCESSED_EVENT_TRIM_TO) : entries;
        sessionStorage.setItem('notif_processed_events', JSON.stringify(toStore));
      } catch {
        // Ignore storage errors (e.g., private browsing quota exceeded)
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Set up event listeners for decrypted, membership, and reaction events
  useEffect(() => {
    if (!client) {
      logger.debug('[useNotificationEvents] No client available');
      return;
    }

    // Store handlers in refs for cleanup
    decryptedHandlerRef.current = handleDecryptedEvent;
    membershipHandlerRef.current = handleMemberEvent;
    reactionHandlerRef.current = handleReactionEvent;
    timelineNotifHandlerRef.current = handleTimelineForNotifications;

    // Subscribe to events
    client.on(MatrixEventEvent.Decrypted, decryptedHandlerRef.current);
    client.on(RoomMemberEvent.Membership, membershipHandlerRef.current);
    client.on(RoomEvent.Timeline, reactionHandlerRef.current);
    client.on(RoomEvent.Timeline, timelineNotifHandlerRef.current);

    logger.debug('[useNotificationEvents] Subscribed to Matrix events');

    // Cleanup
    return () => {
      if (decryptedHandlerRef.current) {
        client.off(MatrixEventEvent.Decrypted, decryptedHandlerRef.current);
      }
      if (membershipHandlerRef.current) {
        client.off(RoomMemberEvent.Membership, membershipHandlerRef.current);
      }
      if (reactionHandlerRef.current) {
        client.off(RoomEvent.Timeline, reactionHandlerRef.current);
      }
      if (timelineNotifHandlerRef.current) {
        client.off(RoomEvent.Timeline, timelineNotifHandlerRef.current);
      }
      pendingEncryptedEventIds.clear();
      logger.debug('[useNotificationEvents] Unsubscribed from Matrix events');
    };
  }, [client, handleDecryptedEvent, handleMemberEvent, handleReactionEvent, handleTimelineForNotifications]);

  // Set up room account data listeners for read marker cancellation
  useEffect(() => {
    if (!client) return;

    accountDataHandlerRef.current = handleAccountData;

    // Subscribe to account data for all existing rooms
    const rooms = client.getRooms();
    rooms.forEach((room) => {
      if (!subscribedRoomsRef.current.has(room.roomId)) {
        room.on(RoomEvent.AccountData, accountDataHandlerRef.current);
        subscribedRoomsRef.current.add(room.roomId);
      }
    });

    // Subscribe to ClientEvent.Room to pick up newly joined rooms
    const onNewRoom = (room) => {
      if (!subscribedRoomsRef.current.has(room.roomId)) {
        room.on(RoomEvent.AccountData, accountDataHandlerRef.current);
        subscribedRoomsRef.current.add(room.roomId);
        logger.debug(`[useNotificationEvents] Subscribed to AccountData for new room ${room.roomId}`);
      }
    };
    client.on(ClientEvent.Room, onNewRoom);

    // Capture ref value for cleanup
    const subscribedRooms = subscribedRoomsRef.current;

    logger.debug(`[useNotificationEvents] Subscribed to AccountData for ${subscribedRooms.size} rooms`);

    // Cleanup
    return () => {
      client.off(ClientEvent.Room, onNewRoom);
      if (accountDataHandlerRef.current) {
        subscribedRooms.forEach((roomId) => {
          const room = client.getRoom(roomId);
          if (room) {
            room.off(RoomEvent.AccountData, accountDataHandlerRef.current);
          }
        });
        subscribedRooms.clear();
      }
      logger.debug('[useNotificationEvents] Unsubscribed from room AccountData events');
    };
  }, [client, handleAccountData]);
};

export default useNotificationEvents;
